import type { 
  SearchRequest, 
  SearchResponse, 
  Candidate, 
  ApiResponse 
} from '../shared/types';
import { FrontendSearchService } from './FrontendSearchService';

// Initialize the frontend search service
const frontendSearchService = new FrontendSearchService();

// Simulate API response delay for realistic behavior
const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper to wrap responses in API format
const createApiResponse = <T>(data: T, message: string): ApiResponse<T> => ({
  success: true,
  data,
  message
});

const createErrorResponse = <T = any>(error: string, message: string): ApiResponse<T> => ({
  success: false,
  error,
  message
} as ApiResponse<T>);

export const localSearchAPI = {
  searchCandidates: async (searchRequest: SearchRequest): Promise<ApiResponse<SearchResponse>> => {
    console.log('🔧 Using local frontend search service');
    
    try {
      // Add a small delay to simulate network request
      await delay(200);
      
      const startTime = Date.now();
      const matches = await frontendSearchService.searchCandidates(searchRequest);
      const searchTime = Date.now() - startTime;
      
      const response = createApiResponse<SearchResponse>({
        matches,
        totalFound: matches.length,
        searchTime,
        query: searchRequest
      }, `Found ${matches.length} matching students`);
      
      return response;
    } catch (error) {
      console.error('Local search error:', error);
      return createErrorResponse<SearchResponse>(
        'Search failed',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  },

  testSearch: async (): Promise<ApiResponse<SearchResponse>> => {
    console.log('🔧 Using local frontend test search');
    
    // Create a default test search request
    const testRequest: SearchRequest = {
      jobDescription: {
        title: 'Software Engineer',
        company: 'Test Company',
        description: 'Looking for a skilled software engineer with experience in React and Node.js',
        requirements: ['React', 'Node.js', 'JavaScript'],
        experienceLevel: 'mid',
        remoteOk: true
      },
      limit: 5
    };
    
    return localSearchAPI.searchCandidates(testRequest);
  },
};

export const localCandidatesAPI = {
  getAllCandidates: async (params?: {
    limit?: number;
    offset?: number;
    skills?: string;
    location?: string;
  }): Promise<ApiResponse<{
    candidates: Candidate[];
    total: number;
    limit: number;
    offset: number;
  }>> => {
    console.log('🔧 Using local frontend candidates service');
    
    try {
      await delay(100);
      
      const result = frontendSearchService.getAllCandidates(params);
      
      return createApiResponse(result, `Retrieved ${result.candidates.length} students`);
    } catch (error) {
      console.error('Local get candidates error:', error);
      return createErrorResponse<{
        candidates: Candidate[];
        total: number;
        limit: number;
        offset: number;
      }>(
        'Failed to retrieve candidates',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  },

  getCandidateById: async (id: string): Promise<ApiResponse<Candidate>> => {
    console.log('🔧 Using local frontend get candidate by ID service');
    
    try {
      await delay(50);
      
      const candidate = frontendSearchService.getCandidateById(id);
      
      if (!candidate) {
        return createErrorResponse<Candidate>(
          'Student not found',
          `No student found with ID: ${id}`
        );
      }
      
      return createApiResponse(candidate, 'Student retrieved successfully');
    } catch (error) {
      console.error('Local get candidate by ID error:', error);
      return createErrorResponse<Candidate>(
        'Failed to retrieve candidate',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  },

  getUniqueSkills: async (): Promise<ApiResponse<string[]>> => {
    console.log('🔧 Using local frontend skills service');
    
    try {
      await delay(50);
      
      const skills = frontendSearchService.getUniqueSkills();
      
      return createApiResponse(skills, `Found ${skills.length} unique skills`);
    } catch (error) {
      console.error('Local get skills error:', error);
      return createErrorResponse<string[]>(
        'Failed to retrieve skills',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  },

  getUniqueLocations: async (): Promise<ApiResponse<string[]>> => {
    console.log('🔧 Using local frontend locations service');
    
    try {
      await delay(50);
      
      const locations = frontendSearchService.getUniqueLocations();
      
      return createApiResponse(locations, `Found ${locations.length} unique locations`);
    } catch (error) {
      console.error('Local get locations error:', error);
      return createErrorResponse<string[]>(
        'Failed to retrieve locations',
        error instanceof Error ? error.message : 'Unknown error occurred'
      );
    }
  },
};

export const localHealthAPI = {
  checkHealth: async (): Promise<any> => {
    console.log('🔧 Using local frontend health check');
    
    await delay(30);
    
    return {
      success: true,
      message: 'Local frontend service is healthy',
      timestamp: new Date().toISOString(),
      service: 'frontend-local'
    };
  },
};
